import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { AuthPageShell } from '../components/auth/AuthPageShell';
import Input from '../components/ui/Input';
import { post } from '../shared/apiClient';
import { getQueryParam } from '../shared/queryParams';
import { useAuth } from '../contexts/AuthContext';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type Phase = 'email' | 'password' | 'unverified';

const SignInPage: React.FC = () => {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const prefillEmail = getQueryParam(location.search, 'email') ?? '';

  const [phase, setPhase] = useState<Phase>(prefillEmail ? 'password' : 'email');
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim()) return;
      if (!EMAIL_RE.test(email.trim())) {
        setError(t('auth:sign_in.invalid_email'));
        return;
      }
      setError(null);
      setSubmitting(true);
      try {
        const r = await post<{ email: string }, { status: string }>(
          '/auth/lookup-email',
          { email: email.trim() },
        );
        const { status } = r.data;
        if (status === 'not_found') {
          navigate(`/register?email=${encodeURIComponent(email.trim())}`);
        } else if (status === 'needs_claim') {
          navigate('/claim-account');
        } else if (status === 'unverified') {
          setPhase('unverified');
        } else {
          setPhase('password');
        }
      } catch {
        setError(t('errors:generic'));
      } finally {
        setSubmitting(false);
      }
    },
    [email, navigate, t],
  );

  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const handleResendVerification = useCallback(async () => {
    setError(null);
    setResendSuccess(false);
    setSubmitting(true);
    try {
      await post('/auth/resend-verification', { email: email.trim() });
      setResendCooldown(60);
      setResendSuccess(true);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : t('auth:sign_in.resend_failed');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [email, t]);

  const handlePasswordSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!password) return;
      setError(null);
      setSubmitting(true);
      try {
        await login(email, password);
        navigate('/client');
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : t('auth:sign_in.invalid_credentials');
        setError(msg);
      } finally {
        setSubmitting(false);
      }
    },
    [email, password, login, navigate, t],
  );

  return (
    <AuthPageShell>
      <div className="flex w-full max-w-[608px] flex-col gap-8 rounded-lg border border-[#E2E8F0] bg-[#F9FAFB] p-6">
        <h1 className="text-center text-[22px] font-semibold leading-tight text-gray-900">
          {t('auth:sign_in.title')}
        </h1>

        {phase === 'email' ? (
          <form className="flex min-w-0 flex-col gap-8" onSubmit={handleEmailSubmit} noValidate>
            <Input
              id="signin-email"
              name="email"
              type="email"
              autoComplete="email"
              label={t('auth:sign_in.enter_email')}
              labelClassName="text-[#131315]"
              placeholder={t('auth:shared.email_placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="!focus:border-teal-500 !focus:ring-teal-500"
            />
            {error ? (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            ) : null}
            <div className="mx-auto flex w-full max-w-[400px] flex-col gap-4">
              <button
                type="submit"
                disabled={!email.trim() || submitting}
                className="w-full rounded-md border border-[#6ECFCF] bg-[#6ECFCF] px-4 py-3 text-sm font-semibold text-gray-900 transition-colors hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[#6ECFCF] focus:ring-offset-2 disabled:opacity-50"
              >
                {submitting ? t('auth:sign_in.checking') : t('common:buttons.continue')}
              </button>
              <Link
                to="/register"
                className="flex w-full items-center justify-center rounded-md border border-[#939393] bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-sm transition-colors hover:bg-gray-50"
              >
                {t('auth:sign_in.create_account')}
              </Link>
            </div>
          </form>
        ) : phase === 'unverified' ? (
          <div className="flex flex-col gap-6 text-center">
            <div className="flex flex-col gap-1 text-sm text-gray-600">
              <p>
                <Trans
                  i18nKey="auth:sign_in.unverified_sent"
                  values={{ email }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <p>{t('auth:sign_in.unverified_click')}</p>
              <p className="text-gray-400">{t('auth:sign_in.unverified_spam')}</p>
            </div>
            {resendSuccess ? (
              <p className="text-sm text-green-600">{t('auth:sign_in.resend_success')}</p>
            ) : null}
            {error ? (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            ) : null}
            <div className="mx-auto flex w-full max-w-[400px] flex-col gap-4">
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendCooldown > 0 || submitting}
                className="w-full rounded-md border border-[#6ECFCF] bg-[#6ECFCF] px-4 py-3 text-sm font-semibold text-gray-900 transition-colors hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[#6ECFCF] focus:ring-offset-2 disabled:opacity-50"
              >
                {resendCooldown > 0
                  ? t('auth:sign_in.resend_in', { seconds: resendCooldown })
                  : submitting
                    ? t('auth:sign_in.sending')
                    : t('auth:sign_in.resend_link')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPhase('email');
                  setError(null);
                }}
                className="text-center text-sm text-gray-500 hover:underline"
              >
                {t('auth:sign_in.use_different_email')}
              </button>
            </div>
          </div>
        ) : (
          <form className="flex min-w-0 flex-col gap-8" onSubmit={handlePasswordSubmit} noValidate>
            <p className="text-sm text-gray-600 text-center">{email}</p>
            <Input
              id="signin-password"
              name="password"
              type="password"
              autoComplete="current-password"
              label={t('auth:sign_in.enter_password')}
              labelClassName="text-[#131315]"
              placeholder={t('auth:shared.password_placeholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="!focus:border-teal-500 !focus:ring-teal-500"
            />
            {error ? (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            ) : null}
            <div className="mx-auto flex w-full max-w-[400px] flex-col gap-4">
              <button
                type="submit"
                disabled={!password || submitting}
                className="w-full rounded-md border border-[#6ECFCF] bg-[#6ECFCF] px-4 py-3 text-sm font-semibold text-gray-900 transition-colors hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[#6ECFCF] focus:ring-offset-2 disabled:opacity-50"
              >
                {submitting ? t('auth:sign_in.signing_in') : t('auth:sign_in.title')}
              </button>
              <Link
                to="/forgot-password"
                className="text-center text-sm text-gray-600 hover:underline"
              >
                {t('auth:sign_in.forgot_password')}
              </Link>
              <button
                type="button"
                onClick={() => {
                  setPhase('email');
                  setPassword('');
                  setError(null);
                }}
                className="text-center text-sm text-gray-500 hover:underline"
              >
                {t('auth:sign_in.use_different_email')}
              </button>
            </div>
          </form>
        )}
      </div>
    </AuthPageShell>
  );
};

export default SignInPage;
